import { Component, OnDestroy, OnInit } from '@angular/core';
import { IAccount } from '../../core/models/account.model';
import { DynamicTableComponent } from '../../shared/components/dynamic-table/dynamic-table.component';
import { getDefaultPageable, Pageable, PageParams } from '../../core/models/page.model';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AccountService } from '../../core/services/account.service';
import { ToastService } from '../../core/services/toast.service';
import {customConfirmationDialog, customDynamicDialog, DEFAULT_ROWS_PER_PAGE_OPTIONS} from '../../core/utils/utils';
import { CustomDialogData } from '../../core/models/custom-dialog.model';
import { take } from 'rxjs';
import {PaginatorModule, PaginatorState} from 'primeng/paginator';
import { AccountDetailsComponent } from './account-details/account-details.component';
import { ToolbarComponent } from '../../shared/components/toolbar/toolbar.component';
import { CustomAction, CustomTableHeader } from '../../core/models/custom-table.model';
import {IInstitution} from "../../core/models/institution.model";
import {IBank} from "../../core/models/bank.model";
import {InstitutionService} from "../../core/services/institution.service";
import {BankService} from "../../core/services/bank.service";
import {Button} from "primeng/button";
import {CardModule} from "primeng/card";
import {SelectModule} from "primeng/select";
import {ConfirmationService, PrimeTemplate} from "primeng/api";
import {FormBuilder, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {TooltipModule} from "primeng/tooltip";
import {NgIf} from "@angular/common";
import {ProgressSpinnerModule} from "primeng/progressspinner";
import {environment} from "../../../environments/environment.development";
import {ICurrency} from "../../core/models/devise.model";

@Component({
    selector: 'app-account',
    imports: [
        DynamicTableComponent,
        ToolbarComponent,
        Button,
        CardModule,
        SelectModule,
        PaginatorModule,
        PrimeTemplate,
        ReactiveFormsModule,
        TooltipModule,
        NgIf,
        ProgressSpinnerModule
    ],
    templateUrl: './account.component.html',
    styleUrls: ['./account.component.scss']
})
export class AccountComponent implements OnInit, OnDestroy {

  accounts: IAccount[] = [];
  columns: CustomTableHeader[] = [
    {
      key: 'institutionName',
      column: 'Institution',
      type: 'text',
      filterable: true,
      sortable: true
    },
    {
      key: 'bank.swiftCode',
      column: 'BIC',
      type: 'text',
      filterable: true,
      sortable: true
    },
    {
      key: 'bank.name',
      column: 'Banque',
      type: 'text',
      filterable: true,
      sortable: true
    },
    {
      key: 'type',
      column: 'Type',
      type: 'text',
      filterable: true,
      sortable: true
    },
    {
      key: 'libelle',
      column: 'Libellé',
      type: 'text',
      filterable: true,
      sortable: true
    },
    {
      key: 'number',
      column: 'Numéro',
      type: 'text',
      filterable: true,
      sortable: true
    },
    {
      key: 'lastBalance.balance',
      column: 'Solde',
      type: 'number',
      filterable: true,
      sortable: true
    },
    {
      key: 'currency.code',
      column: 'Devise',
      type: 'text',
      filterable: true,
      sortable: true
    },

  ]
  pageable: PageParams = { page: 0, first: 0, rows: 0, totalRecords: 0, rowPerPageOptions: [] };
  ref: DynamicDialogRef | null = null;
  actions: CustomAction[] = [];
  toolbarActions: CustomAction[] = [];
  accountTypes = [
    {type: "Local"},
    {type: "Nostro"},
    {type: "Etranger"},
    {type: "Hello"}
  ]
  institutions: IInstitution[] = []
  banks: IBank[] = []
  accountsToDisplay: IAccount[] = [];
  hasSearched = false
  searchForm!: FormGroup
  displaySpinner = false
  production = environment.production
  constructor(
    private readonly accountService: AccountService,
    private readonly toastService: ToastService,
    private readonly dialogService: DialogService,
    private readonly institutionService: InstitutionService,
    private readonly bankService: BankService,
    private formBuilder: FormBuilder,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit() {
    this.initForm();
    this.toolbarActions = [
      {
        role: 'CREATE_ACCOUNT',
        icon: 'pi pi-plus',
        label: 'Nouveau',
        severity: 'primary',
        emit: () => this.createAccount(),
        visible: true
      }
    ];
    this.actions = this.getActions();
    this.loadInstitutions();
    this.loadBanks();
  }

  initForm() {
    this.searchForm = this.formBuilder.group({
      institution: null,
      bank: null,
      type: null
    })
  }

  loadInstitutions(pageable = getDefaultPageable()) {
    pageable.unpaged = true
    this.institutionService.getAllInstitutions(pageable).subscribe({
      next: page => {
        this.institutions = page.content
      },
      error: err => {
        console.log(err)
      }
    })
  }

  loadBanks(pageable = getDefaultPageable()) {
    pageable.unpaged = true
    this.bankService.getAllBanks(pageable).subscribe({
      next: page => {
        this.banks = page.content
      },
      error: err => {
        console.log(err)
      }
    })
  }

  resetInstitutionDropDown() {

  }

  onSearch(pageable = getDefaultPageable()) {
    this.hasSearched = true
    this.displaySpinner = true
    this.accountService.searchAccountsByMultipleCriteria(this.searchForm.value, pageable).subscribe({
      next: page => {
        this.accountsToDisplay = page.content
        this.displaySpinner = false
        if (!this.production) console.log(this.accountsToDisplay)
        this.pageable = {
          page: page.number,
          first: page.number * page.size,
          rows: page.size,
          totalRecords: page.totalElements,
          rowPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS
        }
      },
      error: err => {
        if (!this.production) console.log(err)
        this.displaySpinner = false
      }
    })
  }

  onReset() {
    this.searchForm.reset()
    this.hasSearched = false
    this.accountsToDisplay = []

  }


  ngOnDestroy() {
    this.ref?.close();
  }

  getActions(): CustomAction[] {
    return [
      {
        role: 'READ_ACCOUNT',
        icon: 'pi pi-info-circle',
        label: 'Consulter',
        severity: 'info',
        emit: (item) => this.viewOrEditAccount(item),
        visible: true
      },
      {
        role: 'UPDATE_ACCOUNT',
        icon: 'pi pi-pen-to-square',
        label: 'Modifier',
        severity: 'primary',
        emit: (item) => this.viewOrEditAccount(item, true),
        visible: true
      },
      {
        role: 'DELETE_ACCOUNT',
        icon: 'pi pi-trash',
        label: 'Supprimer',
        severity: 'danger',
        emit: (item: IAccount) => this.confirmDeletion(item),
        visible: true
      }
    ];
  }

  loadAccounts(doRefresh?: boolean, pageable: Pageable = getDefaultPageable()) {
    this.accountService.searchAccountsByMultipleCriteria(this.searchForm.value, pageable).subscribe({
      next: page => {
        this.accounts = page.content;
        console.log(this.accounts)

        if (doRefresh)
          this.accountsToDisplay = this.accounts

        this.pageable = {
          page: page.number,
          first: page.number * page.size,
          rows: page.size,
          totalRecords: page.totalElements,
          rowPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS
        }
      },
      error: err => {
        this.toastService.showToast('Erreur lors du chargement des comptes', err.error, 'error');
      }
    })
  }

  createAccount() {
    const data = { isCreate: true } as CustomDialogData;
    this.ref = this.dialogService.open(AccountDetailsComponent, customDynamicDialog('Créer un compte', data));
    this.ref?.onClose.pipe(take(1)).subscribe(() => this.loadAccounts());
  }

  viewOrEditAccount(account: IAccount, isEdit: boolean = false) {
    const data = { isView: !isEdit, isEdit, data: account } as CustomDialogData;
    this.ref = this.dialogService.open(AccountDetailsComponent, customDynamicDialog('Détails du compte', data));
    this.ref?.onClose.pipe(take(1)).subscribe(() => this.loadAccounts());
  }

  confirmDeletion(account: IAccount) {
    const header = 'Suppression d\'un compte';
    const message = `Êtes-vous sûr de vouloir supprimer le compte << <strong> ${account.libelle} </strong> >> ?`;
    const options = customConfirmationDialog(header, message, { accept: () => this.deleteAccount(account) })
    this.confirmationService.confirm(options)
  }

  deleteAccount(account: IAccount) {
    if (!account.id) {
      this.toastService.showToast('Erreur lors de la suppression du compte', 'Impossible de supprimer le compte', 'error');
      return;
    }
    this.accountService.deleteAccount(account.id).subscribe({
      next: () => {
        this.toastService.showToast('Compte supprimé', `Le compte <strong> << ${account.libelle} >> </strong> a été supprimé avec succès.`);
        this.loadAccounts(true);
      },
      error: err => {
        this.toastService.showToast('Erreur lors de la suppression du compte', err.error, 'error');
      }
    });
  }

  onPageChange = (event: PaginatorState) => {
    if(this.hasSearched) {
      this.loadAccounts(true, getDefaultPageable(event.page, event.rows))
    }
  };
}
